import dayjs from "$lib/utils/dayjs-config.js";
import { showToast } from "$lib/utils/plugins/toast/toast";
import { goto } from "$app/navigation";
import { local } from "$lib/client-data/local.js";
import { showModal } from "$lib/components/Modal";

/**
 * 防抖函数
 * @param {Function} func 要执行的函数
 * @param {number} wait 延迟时间（毫秒）
 * @returns {Function} 防抖后的函数
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * 节流函数
 * @param {Function} func 要执行的函数
 * @param {number} limit 限制时间（毫秒）
 * @returns {Function} 节流后的函数
 */
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * 生成唯一ID
 * @returns {string} 唯一ID
 */
export function generateId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * 检查是否为移动设备
 * @returns {boolean} 是否为移动设备
 */
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

/**
 * 安全的 JSON 解析
 * @param {string} str 要解析的字符串
 * @param {any} defaultValue 解析失败时的默认值
 * @returns {any} 解析结果
 */
export function safeJSONParse(str, defaultValue = null) {
  try {
    return JSON.parse(str);
  } catch (error) {
    console.error("JSON parse error:", error);
    return defaultValue;
  }
}

/**
 * 深度克隆对象
 * @param {any} obj 要克隆的对象
 * @returns {any} 克隆后的对象
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== "object") return obj;
  if (obj instanceof Date) return new Date(obj);
  if (obj instanceof Array) return obj.map((item) => deepClone(item));
  if (obj instanceof Object) {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, deepClone(value)]));
  }
  return obj;
}

/**
 * 获取日期的开始时间（00:00:00.000）
 * 使用 dayjs 替代 new Date(date).setHours(0, 0, 0, 0)
 * 性能优化：避免创建多余的 Date 对象
 * 兼容性：dayjs 提供更好的跨浏览器兼容性
 *
 * @param {string|number|Date} date 输入日期
 * @returns {number} 返回时间戳（毫秒）
 */
export function getDayStart(date) {
  return dayjs(date).startOf("day").valueOf();
}

/**
 * 获取日期的结束时间（23:59:59.999）
 * 使用 dayjs 替代 new Date(date).setHours(23, 59, 59, 999)
 * 性能优化：避免创建多余的 Date 对象
 * 兼容性：dayjs 提供更好的跨浏览器兼容性
 *
 * @param {string|number|Date} date 输入日期
 * @returns {number} 返回时间戳（毫秒）
 */
export function getDayEnd(date) {
  return dayjs(date).endOf("day").valueOf();
}

/**
 * 获取日期的开始和结束时间
 * 一次性获取开始和结束时间，避免多次解析同一个日期
 * 性能优化：减少 dayjs 对象的创建次数
 *
 * @param {string|number|Date} date 输入日期
 * @returns {[number, number]} 返回 [开始时间戳, 结束时间戳]
 */
export function getDayRange(date) {
  const d = dayjs(date);
  return [d.startOf("day").valueOf(), d.endOf("day").valueOf()];
}

// 两个时间差转换成 1d ...
export const getPostTime = (t, l) => {
  //注意parseInt会错误地把1.1574074074074074e-8解析为1
  //兼容系统时间可能返回有误差，如果t(系统时间)>l(当前时间)则应该返回0
  let minusTime = l - t;
  if (minusTime <= 0) return "0秒";
  var d = Math.floor(minusTime / 1000 / 3600 / 24);
  var h = Math.floor((minusTime / 1000 / 3600) % 24);
  var m = Math.floor((minusTime / 1000 / 60) % 60);
  var s = Math.floor((minusTime / 1000) % 60);
  if (d > 0) return d + "天";
  if (h > 0) return h + "小时";
  if (m > 0) return m + "分钟";
  else return s + "秒";
};

/**
 * isArry 是否返回波色数组 默认false
 */
export const getLhcColor = (num, isArry = false, isNumType = false) => {
  //isArry 是否返回波色数组 默认false
  let arr = [
    {
      color: "red",
      text: "红",
      nums: ["01", "02", "07", "08", "12", "13", "18", "19", "23", "24", "29", "30", "34", "35", "40", "45", "46"],
    },
    {
      color: "blue",
      text: "蓝",
      nums: ["03", "04", "09", "10", "14", "15", "20", "25", "26", "31", "36", "37", "41", "42", "47", "48"],
    },
    {
      color: "green",
      text: "绿",
      nums: ["05", "06", "11", "16", "17", "21", "22", "27", "28", "32", "33", "38", "39", "43", "44", "49"],
    },
  ];

  if (isArry) {
    return arr;
  }

  let result = arr.filter((item) => item.nums.map((item) => Number(item)).includes(Number(num)));
  if (isNumType) {
    return result.length ? result[0] : {};
  } else {
    return result.length ? result[0].color : "";
  }
};

/**
 * 精度安全的数字处理通用函数
 * @param {null|undefined|number|string} target
 * @param {number} decimal - 保留的小数位数
 * @param {boolean} returnNumber - 是否返回数字类型
 * @returns {string|number|''} 返回处理后的数字
 */
function safePrecisionNumber(target, decimal = 3, returnNumber = false) {
  if (target === null || target === undefined || target === "" || isNaN(Number(target))) {
    return returnNumber ? 0 : "";
  }

  // 转换为字符串进行处理，避免浮点数精度问题
  let numStr = String(target).trim();

  // 处理科学计数法
  if (numStr.includes("e") || numStr.includes("E")) {
    numStr = Number(numStr).toFixed(20); // 先转换为足够精度的小数
  }

  // 处理负号
  const isNegative = numStr.startsWith("-");
  if (isNegative) {
    numStr = numStr.slice(1);
  }

  // 分离整数和小数部分
  const parts = numStr.split(".");
  let integerPart = parts[0] || "0";
  let decimalPart = parts[1] || "";

  // 确保 decimal 是有效的非负整数
  const validDecimal = Math.max(0, Math.floor(decimal));

  if (validDecimal === 0) {
    // 不保留小数位，直接返回整数部分
    const result = (isNegative ? "-" : "") + integerPart;
    return returnNumber ? Number(result) : result;
  }

  // 截断或补齐小数部分到指定位数
  if (decimalPart.length > validDecimal) {
    // 截断（使用 Math.floor 的逻辑，不四舍五入）
    decimalPart = decimalPart.slice(0, validDecimal);
  } else {
    // 补齐
    decimalPart = decimalPart.padEnd(validDecimal, "0");
  }

  // 组合结果
  const result = (isNegative ? "-" : "") + integerPart + "." + decimalPart;
  return returnNumber ? Number(result) : result;
}

/**
 * 数据处理（修复精度丢失问题）
 * @param {null|undefined|number|string} target
 * @param {Number} decimal - 保留的小数位数
 * @returns {string|''} 返回字符串格式的数字
 */
export const NumberFormat = (target, decimal = 3) => {
  return safePrecisionNumber(target, decimal, false);
};

export const convertHtmlToText = (htmlString) => {
  const parser = new DOMParser();

  // 使用 DOMParser 解析 HTML 字符串
  const doc = parser.parseFromString(htmlString, "text/html");
  // console.log('🚀🚀🚀🚀 ~ doc:', doc)

  // 获取 body 元素的文本内容
  let bodyContent = doc.body ? doc.body.innerText : "";
  if (bodyContent == "") {
    bodyContent = doc.innerText;
  }
  // console.log('🚀🚀🚀🚀 ~ bodyContent:', bodyContent)

  return bodyContent;
};

/**
 * 获取今天的时间范围
 * @returns {[number, number]} [开始时间戳, 结束时间戳]
 */
export function getTodayRange() {
  // 1. 获取当前日期，格式化为 YYYY-MM-DD
  const today = dayjs().format('YYYY-MM-DD');
  
  // 2. 拼接时分秒，转换为北京时区时间戳
  const startTime = dayjs.tz(`${today} 00:00:00`, 'Asia/Shanghai').valueOf();
  const endTime = dayjs.tz(`${today} 23:59:59`, 'Asia/Shanghai').valueOf();
  
  return [startTime, endTime];
}

/**
 * 获取昨天的时间范围
 * @returns {[number, number]} [开始时间戳, 结束时间戳]
 */
export function getYesterdayRange() {
  // 1. 获取昨天日期，格式化为 YYYY-MM-DD
  const yesterday = dayjs().subtract(1, "day").format('YYYY-MM-DD');
  
  // 2. 拼接时分秒，转换为北京时区时间戳
  const startTime = dayjs.tz(`${yesterday} 00:00:00`, 'Asia/Shanghai').valueOf();
  const endTime = dayjs.tz(`${yesterday} 23:59:59`, 'Asia/Shanghai').valueOf();
  console.log('🚀🚀🚀🚀 ~ getYesterdayRange ~ startTime:', startTime)
  console.log('🚀🚀🚀🚀 ~ getYesterdayRange ~ endTime:', endTime)
  return [startTime, endTime];
}

/**
 * 获取近7天的时间范围
 * @returns {[number, number]} [开始时间戳, 结束时间戳]
 */
export function getLast7DaysRange() {
  // 1. 获取7天前的日期，格式化为 YYYY-MM-DD
  const startDate = dayjs().subtract(6, "day").format('YYYY-MM-DD');
  const endDate = dayjs().format('YYYY-MM-DD');
  
  // 2. 拼接时分秒，转换为北京时区时间戳
  const startTime = dayjs.tz(`${startDate} 00:00:00`, 'Asia/Shanghai').valueOf();
  const endTime = dayjs.tz(`${endDate} 23:59:59`, 'Asia/Shanghai').valueOf();
  
  return [startTime, endTime];
}

/**
 * 获取近30天的时间范围
 * @returns {[number, number]} [开始时间戳, 结束时间戳]
 */
export function getLast30DaysRange() {
  // 1. 获取30天前的日期，格式化为 YYYY-MM-DD
  const startDate = dayjs().subtract(29, "day").format('YYYY-MM-DD');
  const endDate = dayjs().format('YYYY-MM-DD');
  
  // 2. 拼接时分秒，转换为北京时区时间戳
  const startTime = dayjs.tz(`${startDate} 00:00:00`, 'Asia/Shanghai').valueOf();
  const endTime = dayjs.tz(`${endDate} 23:59:59`, 'Asia/Shanghai').valueOf();
  
  return [startTime, endTime];
}

/**
 * 获取本月的时间范围
 * @returns {[number, number]} [开始时间戳, 结束时间戳]
 */
export function getCurrentMonthRange() {
  const now = dayjs();
  return [now.startOf("month").valueOf(), now.endOf("month").valueOf()];
}

/**
 * 获取上月的时间范围
 * @returns {[number, number]} [开始时间戳, 结束时间戳]
 */
export function getLastMonthRange() {
  const lastMonth = dayjs().subtract(1, "month");
  return [lastMonth.startOf("month").valueOf(), lastMonth.endOf("month").valueOf()];
}

/**
 * 获取自定义时间范围
 * @param {number} days 往前推的天数
 * @returns {[number, number]} [开始时间戳, 结束时间戳]
 */
export function getCustomDaysRange(days) {
  const now = dayjs();
  return [
    now
      .subtract(days - 1, "day")
      .startOf("day")
      .valueOf(),
    now.endOf("day").valueOf(),
  ];
}

/**
 * 规范化时间戳（确保为毫秒级）
 * @param {number|string|Date} timestamp 时间戳
 * @returns {number} 规范化后的毫秒级时间戳
 */
export function normalizeTimestamp(timestamp) {
  if (!timestamp) return 0;

  const ts = Number(timestamp);
  if (isNaN(ts)) return 0;

  // 自动识别时间戳单位
  // 如果时间戳小于 1e10（2001-09-09），认为是秒级时间戳，需要转换为毫秒
  // 这个阈值可以有效区分秒级和毫秒级时间戳
  return ts < 1e10 ? ts * 1000 : ts;
}

/**
 * 格式化时间戳为可读字符串
 * @param {number} timestamp 时间戳
 * @param {string} format 格式化模板
 * @returns {string} 格式化后的时间字符串
 */
export function formatTimestamp(timestamp, format = "YYYY-MM-DD HH:mm:ss") {
  const ts = normalizeTimestamp(timestamp);
  if (!ts) return "";

  return dayjs(ts).format(format);
}

/**
 * 格式化时间为友好的显示格式
 * @param {number|string|Date} time 时间
 * @returns {string} 友好的时间显示
 */
export function formatFriendlyTime(time) {
  const now = dayjs();
  const target = dayjs(time);
  const diff = now.diff(target, "minute");

  if (diff < 1) return "刚刚";
  if (diff < 60) return `${diff}分钟前`;

  const hourDiff = now.diff(target, "hour");
  if (hourDiff < 24) return `${hourDiff}小时前`;

  const dayDiff = now.diff(target, "day");
  if (dayDiff < 7) return `${dayDiff}天前`;

  return target.format("YYYY-MM-DD");
}

/**
 * 格式化日期范围为字符串
 * @param {[number, number]} range 时间范围 [开始时间戳, 结束时间戳]
 * @param {string} format 格式化模板
 * @returns {string} 格式化后的日期范围字符串
 */
export function formatDateRange(range, format = "YYYY-MM-DD") {
  const [start, end] = range;
  return `${dayjs(start).format(format)} 至 ${dayjs(end).format(format)}`;
}

/**
 * 获取指定日期是星期几
 * @param {number|string|Date} date 日期
 * @returns {string} 星期几
 */
export function getWeekDay(date) {
  const weekDays = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"];
  return weekDays[dayjs(date).day()];
}

/**
 * 判断是否是同一天
 * @param {number|string|Date} date1 第一个日期
 * @param {number|string|Date} date2 第二个日期
 * @returns {boolean} 是否是同一天
 */
export function isSameDay(date1, date2) {
  return dayjs(date1).isSame(date2, "day");
}

/**
 * 获取两个日期之间的天数
 * @param {number|string|Date} date1 第一个日期
 * @param {number|string|Date} date2 第二个日期
 * @returns {number} 天数
 */
export function getDaysBetween(date1, date2) {
  return Math.abs(dayjs(date1).diff(date2, "day"));
}

/**
 * 计算指定日期距今多少天
 * @param {number|string|Date} date 指定日期
 * @returns {number} 距今天数（正数表示已过去的天数，负数表示未来的天数）
 */
export function getDaysFromNow(date) {
  return dayjs().diff(dayjs(date), "day");
}

/**
 * 计算指定日期距今多少天（当天算第一天）
 * @param {number|string|Date} date 指定日期
 * @returns {number} 距今天数（当天为1，次日为2...）
 */
export function getDaysFromNowIncludeToday(date) {
  return dayjs().diff(dayjs(date), "day") + 1;
}

/**
 * 获取友好的距今天数显示
 * @param {number|string|Date} date 指定日期
 * @returns {string} 友好的显示文本
 */
export function getFriendlyDaysFromNow(date) {
  const days = getDaysFromNow(date);
  if (days === 0) return "今天";
  if (days === 1) return "昨天";
  if (days === -1) return "明天";
  if (days > 0) return `${days}天前`;
  return `${Math.abs(days)}天后`;
}

/**
 * 获取详细的距今时间显示
 * @param {number|string|Date} date 指定日期
 * @returns {string} 详细的时间显示
 */
export function getDetailedTimeFromNow(date) {
  const now = dayjs();
  const target = dayjs(date);
  const days = now.diff(target, "day");
  const hours = now.diff(target, "hour") % 24;
  const minutes = now.diff(target, "minute") % 60;

  if (days === 0) {
    if (hours === 0) {
      if (minutes === 0) return "刚刚";
      return `${minutes}分钟前`;
    }
    return `${hours}小时前`;
  }

  if (days > 0) {
    if (days < 30) return `${days}天前`;
    if (days < 365) return `${Math.floor(days / 30)}个月前`;
    return `${Math.floor(days / 365)}年前`;
  }

  // 未来时间
  if (days > -30) return `${Math.abs(days)}天后`;
  if (days > -365) return `${Math.floor(Math.abs(days) / 30)}个月后`;
  return `${Math.floor(Math.abs(days) / 365)}年后`;
}
/**
 * 获取当前时间的格式化字符串
 * @param {string} format 可选的格式化模板，默认为 'YYYY-MM-DD HH:mm:ss'
 * @returns {string} 格式化后的当前时间字符串
 * @example
 * // 返回格式如：2024-03-21 14:30:45
 * getCurrentDateTime()
 * // 返回自定义格式
 * getCurrentDateTime('YYYY年MM月DD日 HH时mm分ss秒')
 */
export function getCurrentDateTime(format = "YYYY-MM-DD HH:mm:ss") {
  return dayjs().format(format);
}
/**
 * 加密手机号
 * @param {string} phone 手机号
 * @returns {string} 加密后的手机号
 */
export function EncryptPhone(phone) {
  if (phone) {
    return phone.slice(0, 7) + "****" + phone.slice(11);
  } else {
    return "";
  }
}

/**
 * 数据处理（修复精度丢失问题）
 * @param {null|undefined|number|string} target - 输入值，可以是数字或字符串
 * @param {Number} decimal - 保留的小数位数，默认值为3
 * @returns {string} - 返回格式化后的数字字符串或空字符串
 */
export const formatNumber = (target, decimal = 3) => {
  return safePrecisionNumber(target, decimal, false);
};

/**
 * @description: 开始时间转时间戳 2024.1.2 -> 2024-01-02 00:00:00
 * @param {number} start
 * @return {string}  毫秒级1704128400000
 */
export const formatStartTime = (start) => {
  // 1. 获取日期，格式化为 YYYY-MM-DD
  const dateStr = dayjs(start).format('YYYY-MM-DD');
  
  // 2. 拼接时分秒，转换为北京时区时间戳
  return dayjs.tz(`${dateStr} 00:00:00`, 'Asia/Shanghai').valueOf();
};
/**
 * @description: 结束时间转时间戳  2024.1.2 -> 2024-01-28 23:59:59
 * @param {number} end
 * @return {string}  毫秒级1706374799999
 */
export const formatEndTime = (end) => {
  // 1. 获取日期，格式化为 YYYY-MM-DD
  const dateStr = dayjs(end).format('YYYY-MM-DD');
  
  // 2. 拼接时分秒，转换为北京时区时间戳
  return dayjs.tz(`${dateStr} 23:59:59`, 'Asia/Shanghai').valueOf();
};

export const formatDecimal = (num, decimal = 3) => {
  // 使用精度安全的函数，返回数字类型
  return safePrecisionNumber(num, decimal, true);
};

/**
 * 生成 UUID
 * @returns {string} 生成的UUID
 * @author 老版本拷贝过来正常使用，备注：把crypto.randomUUID()替换掉，老版本安卓浏览器不兼容
 */
export function generateUUID() {
  const hex = "0123456789abcdef";
  let uuid = "";
  for (let i = 0; i < 36; i++) {
    if ([8, 13, 18, 23].includes(i)) {
      uuid += "-";
    } else if (i === 14) {
      uuid += "4"; // 第14位固定为 4，代表UUID版本号
    } else if (i === 19) {
      uuid += hex[(Math.random() * 4 + 8) | 0]; // 确保第19位是 8、9、a 或 b
    } else {
      uuid += hex[(Math.random() * 16) | 0];
    }
  }
  return uuid;
}

// UUID
export const uuid = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * 复制文本到剪贴板
 * @param {string} text 要复制的文本
 */
export function copyToClipboard(text, show = true) {
  return navigator.clipboard
    .writeText(text)
    .then(() => {
      if (show) showToast("复制成功");
    })
    .catch((err) => {
      if (show) showToast.error("复制失败");
    });
}

// 是否试玩账号
// member_type int(11) DEFAULT NULL COMMENT '1测试内部账号用户2注册用户3充值用户4游客账户5超管
export const isTestAccount = (userInfo, path) => {
  if (userInfo?.memberId) {
    const memberType = userInfo.memberType;
    if (memberType === 4) {
      showToast("您还未登录正式账号，请注册账号");
      return true;
    } else {
      if (path) {
        goto(path);
      }
      return false;
    }
  } else {
    goto("/");
    return true;
  }
};

/**
 * 打开新窗口
 * @param {string} url 要打开的URL
 * @param {string} target 窗口目标，默认为'_blank'
 * @param {string} windowFeatures 窗口特性，可选
 */
export function isWindowOpenUrl(url, target = "_blank", windowFeatures) {
  let popup = window.open(url, target, windowFeatures);
  if (popup == null || typeof popup == "undefined") {
    // window.alert('当前新窗口已被浏览器拦截，请点击窗口左上角的设置，允许弹出！')
    window.location.href = url;
  }
}

/**
 * 递归转换对象中的Long类型为number
 * @param {any} obj - 要处理的对象
 * @returns {any} 处理后的对象
 */
export const convertLongToNumber = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // 处理Long类型对象
  if (obj && typeof obj === "object" && obj.low !== undefined && obj.high !== undefined) {
    return Number(obj.low);
  }

  // 处理数组
  if (Array.isArray(obj)) {
    return obj.map(convertLongToNumber);
  }

  // 处理普通对象
  if (obj && typeof obj === "object") {
    const result = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = convertLongToNumber(obj[key]);
      }
    }
    return result;
  }

  // 其他类型直接返回
  return obj;
};
// 根据年份获取1-49个数字对应的动物映射关系(默认当前年份(农历))
export const animalMap = (curYear = new Date().getFullYear()) => {
  let animalArr = ["猪", "狗", "鸡", "猴", "羊", "马", "蛇", "龙", "兔", "虎", "牛", "鼠"];
  let initialYear = 2019;
  let firstIndex;
  if (curYear - initialYear <= 0) {
    firstIndex = Math.abs(curYear - initialYear) % 12;
  } else {
    firstIndex = 12 - ((curYear - initialYear) % 12);
  }
  let animalMap = [];
  for (let i = 1; i <= 49; i++) {
    animalMap.push({ index: i, animal: animalArr[firstIndex++] });
    if (firstIndex == 12) firstIndex = 0;
  }
  return animalMap;
};

// 获取年转换金木水火土
export const elementLhc = (num, year) => {
  let lunarYear = year || new Date().getFullYear();
  let resultNum = (60 + (Math.floor(lunarYear / 2) % 30) - Math.floor(num / 2)) % 30;
  if (lunarYear % 2 == 1 && num % 2 == 0) resultNum++;
  if (resultNum > 30 || resultNum < 0) return "空";
  let elementArr = [
    { num: 0, element: "木" },
    { num: 1, element: "水" },
    { num: 2, element: "金" },
    { num: 3, element: "火" },
    { num: 4, element: "木" },
    { num: 5, element: "土" },
    { num: 6, element: "金" },
    { num: 7, element: "火" },
    { num: 8, element: "水" },
    { num: 9, element: "土" },
    { num: 10, element: "金" },
    { num: 11, element: "木" },
    { num: 12, element: "水" },
    { num: 13, element: "土" },
    { num: 14, element: "火" },
    { num: 15, element: "木" },
    { num: 16, element: "水" },
    { num: 17, element: "金" },
    { num: 18, element: "火" },
    { num: 19, element: "木" },
    { num: 20, element: "土" },
    { num: 21, element: "金" },
    { num: 22, element: "火" },
    { num: 23, element: "水" },
    { num: 24, element: "土" },
    { num: 25, element: "金" },
    { num: 26, element: "木" },
    { num: 27, element: "水" },
    { num: 28, element: "土" },
    { num: 29, element: "火" },
    { num: 30, element: "木" },
  ];
  return elementArr.filter((elementObj) => elementObj.num == resultNum)[0].element;
};

export const getAnimalArry = () => {
  return [
    {
      text: "家禽",
      nums: ["牛", "马", "羊", "鸡", "狗", "猪"],
    },
    {
      text: "野兽",
      nums: ["鼠", "虎", "兔", "龙", "蛇", "猴"],
    },
  ];
};

export const getLhcSx = (optYear) => {
  let zodiac = animalMap(optYear);
  let arr = [];
  for (let index = zodiac.length - 1; index > 0; index--) {
    let arr1 = [];
    let zdcarr = zodiac.filter((item) => item.animal == zodiac[index].animal);
    arr1 = zdcarr.map((item) => {
      if (item.index < 10) {
        return "0" + item.index;
      } else {
        return item.index.toString();
      }
    });
    let newSx = { text: zodiac[index].animal, nums: arr1 };
    arr.push(newSx);
    if (arr.length == 12) {
      return arr;
    }
  }
  return arr;
};

export const getLhcWx = (optYear) => {
  let wx = ["金", "木", "水", "火", "土"];
  let arr = [];
  wx.forEach((element) => {
    let arr1 = [];
    for (let i = 1; i <= 49; i++) {
      if (elementLhc(i, optYear) == element) {
        if (i < 10) {
          arr1.push("0" + i);
        } else {
          arr1.push(i.toString());
        }
      }
    }
    let newWx = {
      text: element,
      nums: arr1,
    };
    arr.push(newWx);
  });

  return arr;
};

// 判断是否是数组
export const isArray = (value) => {
  if (Array.isArray) {
    return Array.isArray(value);
  }
  return Object.prototype.toString.call(value) === "[object Array]";
};

/**
 * 将输入值限制在指定的闭区间内
 * @param {number|string} input - 用户输入的值(数字、字符串、负数、浮点数等)
 * @param {number} min - 区间最小值
 * @param {number} max - 区间最大值
 * @return {number} - 返回限制在指定范围内的整数
 */
export function limitToRange(input, min = 0, max = 3) {
  // 尝试将输入转换为数字
  const num = Number(input);

  // 检查转换结果是否为有效数字
  if (isNaN(num)) {
    return min; // 如果无法转换为数字，返回区间最小值
  }

  // 限制在min-max区间
  if (num < min) return min; // 小于最小值返回最小值
  if (num > max) return max; // 大于最大值返回最大值

  // 返回整数结果 (对于区间内的浮点数进行取整)
  return Math.floor(num);
}

/**
 * 计算到期时间并以友好方式显示
 * @param {number|string|Date} expirationDate 到期日期
 * @returns {string} 返回"xx小时到期"或"xx天到期"格式的字符串
 */
export function getExpirationTime(expirationDate) {
  const now = dayjs();

  // 处理时间戳单位问题
  const targetDate = typeof expirationDate === "number" ? normalizeTimestamp(expirationDate) : expirationDate;

  const target = dayjs(targetDate);

  // 如果已经过期，返回已过期
  if (now.isAfter(target)) {
    return "";
  }

  // 计算剩余时间（使用毫秒级精度计算，避免精度丢失）
  const diffMs = target.diff(now, "millisecond");
  const diffMinutes = Math.ceil(diffMs / (1000 * 60)); // 向上取整，保证不少时间
  const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // 根据剩余时间范围返回不同的文本格式
  if (diffMinutes < 60) {
    return `${diffMinutes}分钟到期`;
  } else if (diffHours < 24) {
    return `${diffHours}小时到期`;
  } else {
    return `${diffDays}天到期`;
  }
}

/**
 * 获取当前月份的所有日期，包括前置空白日期
 * @returns {Array} 包含当前月份所有日期的数组，日期格式为YYYY-MM-DD，前置空白日期为{isNull: true}
 */
export function getDatesInCurrentMonth() {
  const currentDate = dayjs();
  const year = currentDate.year();
  const month = currentDate.month();

  // 获取当月第一天
  const firstDayOfMonth = dayjs(new Date(year, month, 1));
  // 获取当月天数
  const daysInMonth = currentDate.daysInMonth();

  // 生成当月所有日期
  const dates = [];
  for (let i = 0; i < daysInMonth; i++) {
    const date = firstDayOfMonth.add(i, "day");
    dates.push(date.format("YYYY-MM-DD"));
  }

  // 获取第一天是星期几(0-6，0表示星期日)
  const firstDayOfWeek = firstDayOfMonth.day();

  // 添加前置空白日期
  for (let i = 0; i < firstDayOfWeek; i++) {
    dates.unshift({
      isNull: true,
    });
  }

  return dates;
}

export function formatDate(date, format = "YYYY-MM-DD") {
  if (date === null || date === undefined || date === "") return "";
  const input = typeof date === "number" ? normalizeTimestamp(date) : date;
  return dayjs(input).tz("Asia/Shanghai").format(format);
}
export function formatMonthDay(date, format = "D") {
  return dayjs(date).format(format);
}
/**
 * 格式化数字，将大数字转换为带单位的字符串表示（修复精度丢失问题）
 * @param {number|string} num - 要格式化的数字
 * @param {number} [decimals=0] - 保留的小数位数
 * @param {boolean} [showPlus=true] - 是否显示"+"符号
 * @return {string} 格式化后的字符串
 */
export const NumberFormatString = (num, decimals = 0, showPlus = true) => {
  // 使用Number转换
  const value = Number(num);

  // 检查无效值，包括0、NaN、null和undefined
  if ((!value && value !== 0) || isNaN(value)) {
    return "";
  }

  // 预先定义常量避免重复计算
  const TEN_THOUSAND = 10000;
  const THOUSAND = 1000;
  const HUNDRED = 100;
  const plusSign = showPlus ? "+" : "";

  let result = "";
  if (value >= TEN_THOUSAND) {
    // 使用精度安全的除法
    const dividedValue = value / TEN_THOUSAND;
    result = safePrecisionNumber(dividedValue, decimals, false);
    if (decimals > 0) {
      // 移除末尾的0，但保留至少一位小数
      result = result.replace(/\.?0+$/, "");
    }
    return result + "万" + plusSign;
  } else if (value >= THOUSAND) {
    const dividedValue = value / THOUSAND;
    result = safePrecisionNumber(dividedValue, decimals, false);
    if (decimals > 0) {
      result = result.replace(/\.?0+$/, "");
    }
    return result + "千" + plusSign;
  } else if (value >= HUNDRED) {
    const dividedValue = value / HUNDRED;
    result = safePrecisionNumber(dividedValue, decimals, false);
    if (decimals > 0) {
      result = result.replace(/\.?0+$/, "");
    }
    return result + "百" + plusSign;
  }

  // 对于小数值，使用精度安全的处理
  if (decimals > 0) {
    return safePrecisionNumber(value, decimals, false);
  }
  return value.toString();
};

/**
 * 将日期加上指定天数并格式化
 * @param {string|number|Date} date 输入日期
 * @param {number} days 要添加的天数
 * @param {string} format 输出格式
 * @returns {string} 格式化后的日期字符串
 */
export function addDaysAndFormat(date, days = 1, format = "YYYY-MM-DD") {
  return dayjs(date).add(days, "day").format(format);
}

// 图片地址添加前缀
export const imgAddPrefix = (www, url) => {
  if (!url) return "";
  if (url.endsWith(".webp")) return url;
  if (url.startsWith("http")) {
    return "/imgurl?url=" + url;
  }
  return "/imgurl?url=" + (www || "") + url;
};

// 金额显示格式化（修复精度丢失问题）
export const formatNumberAmount = (num) => {
  // 使用精度安全的函数处理到两位小数
  const formattedNum = safePrecisionNumber(num, 2, false);
  if (!formattedNum) return "0";

  const [integer, decimal] = formattedNum.split(".");
  const formattedInt = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // 处理小数部分，去除不必要的末尾0
  if (decimal && decimal !== "00") {
    // 去掉末尾的0，但至少保留一位有效数字
    const trimmedDecimal = decimal.replace(/0+$/, "");
    return trimmedDecimal ? `${formattedInt}.${trimmedDecimal}` : formattedInt;
  }

  return formattedInt;
};

// socket 房间类型
export const wsRoomType = (type) => {
  switch (type) {
    case "ChatTypeLive": // 直播间
      return "live_";
    case "ChatTypeSport": // 滚球
      return "sportrolling_";
    case "ChatTypeLottery": // 热门彩票-----------
      return "hotlottery_";
    case "ChatTypeWOS": // 竞彩
      return "sportlottery_";
    case "ChatTypeSevers": // 客服
      return "service_";
    case "ChatTypeInteractive": // 聊天室
      return "interactive_";
    default:
      return "";
  }
};

/**
 * 计算变量占用的内存大小
 * @param {any} value - 要计算内存大小的变量
 * @returns {string} - 返回格式化的内存大小字符串
 */
export function getMemorySize(value) {
  const seen = new WeakSet();

  function calculateSize(val) {
    // 处理基本类型
    if (val === null) return 0;
    if (typeof val === "boolean") return 4;
    if (typeof val === "number") return 8;
    if (typeof val === "string") return val.length * 2;
    if (typeof val === "undefined") return 0;

    // 处理对象类型
    if (typeof val === "object") {
      // 处理循环引用
      if (seen.has(val)) return 0;
      seen.add(val);

      let size = 0;

      // 处理数组
      if (Array.isArray(val)) {
        size = val.reduce((acc, item) => acc + calculateSize(item), 0);
        return size + val.length * 4; // 数组额外开销
      }

      // 处理普通对象
      for (const key in val) {
        if (Object.prototype.hasOwnProperty.call(val, key)) {
          size += calculateSize(key); // 计算键的大小
          size += calculateSize(val[key]); // 计算值的大小
        }
      }

      return size;
    }

    // 处理函数
    if (typeof val === "function") {
      return val.toString().length * 2;
    }

    return 0;
  }

  const bytes = calculateSize(value);

  // 格式化输出
  if (bytes < 1024) {
    return bytes + " B";
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + " KB";
  } else if (bytes < 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  } else {
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB";
  }
}

/**
 * 无痕跳转
 * @param {string} [route] - URL路径地址
 * 从当前页面跳转到其他页面时，清除当前页面的路由历史，适用于二级、三级页面使用
 * A => B => C => B
 * 从 C 跳转到 B 时，清除 C 的路由历史
 */
export function jumpTraceless(route) {
  console.log("===> jumpTraceless: ", route);
  goto(route, {
    replaceState: true,
    keepFocus: true,
    noScroll: true,
  });
}

/**
 * 将毫秒转换为时分秒格式
 * @param {number} milliseconds - 毫秒数
 * @param {boolean} [showZero=true] - 是否显示零值单位
 * @returns {string} 格式化后的时间字符串，如 "01:30:45"
 */
export function formatMillisecondsToTime(milliseconds, showZero = true) {
  if (!milliseconds || milliseconds < 0) return "00:00:00";

  const duration = dayjs.duration(milliseconds);
  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  // 如果小时为0且不显示零值单位，则只显示分:秒
  if (!showZero && hours === 0) {
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  // 始终显示时:分:秒格式
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

export const myParseInt = (target, decimal = 0) => {
  // 使用精度安全的函数，返回字符串格式的数字
  const result = safePrecisionNumber(target, decimal, false);
  return result || "0";
};

// 是否试玩账号
export const getIsTestAccount = (path) => {
  const userInfo = local.user || {};
  if (userInfo.memberId) {
    const memberType = userInfo.memberType || 4;
    if (memberType == 4) {
      visitorLoginTips();
      return true;
    } else {
      if (path) {
        goto(path);
      }
      return false;
    }
  } else if (!userInfo.token) {
    goto("/auth/login");
    return true;
  }
  return false;
};

// 游客验证提示
export const visitorLoginTips = () => {
  showModal({
    title: "温馨提示",
    login: true,
    status: "warn",
    content: `<div>您还未登录正式账号，请注册账号</div>`,
    confirmText: "立即注册",
    cancelText: "继续试玩",
    onConfirm: () => {
      goto("/auth/reg");
    },
  });
};

/**
 * 数字补0函数
 * @param {number|string} num 要补0的数字
 * @param {number} length 补0后的长度，默认为2
 * @returns {string} 补0后的字符串
 */
export function padZero(num, length = 2) {
  if (num == 0) return "0";
  return num.toString().padStart(length, "0");
}

// 时区转换
export const convertTimeZone = (date, targetZone = "Asia/Shanghai") => {
  return dayjs(date).tz(targetZone);
};

/**
 * 点击按钮执行添加动画
 * @param {MouseEvent} event - The click event from the .btn-add button.
 */
export function animateButtonToInput(event, inputEl) {
  // Get the clicked button
  const btn = event.currentTarget;
  if (!btn || !inputEl) return;
  const btnRect = btn.getBoundingClientRect();
  const inputRect = inputEl.getBoundingClientRect();
  const opacity = 0.5;
  const scale = 0.4;
  const time = 600;

  // Calculate initial position relative to viewport
  const initialX = btnRect.left;
  const initialY = btnRect.top;

  // Calculate target position relative to viewport
  const targetX = inputRect.left - (btnRect.width * scale) / 2 - 6;
  const targetY = inputRect.top - (btnRect.height * scale) / 2 + 14.5;

  // Calculate translation values (difference between target and initial)
  const translateX = targetX - initialX;
  const translateY = targetY - initialY;
  // Create the animated element
  const am = document.createElement("div");
  am.className = "button-am";
  am.style.position = "fixed";
  // 使用transform代替top和left设置初始位置
  am.style.transform = "translate(0, 0)";
  am.style.left = initialX + "px";
  am.style.top = initialY + "px";
  am.style.width = btnRect.width + "px";
  am.style.height = btnRect.height + "px";
  am.style.lineHeight = btnRect.height + "px";
  am.style.borderRadius = window.getComputedStyle(btn).borderRadius;
  am.style.textAlign = "center";
  am.style.zIndex = 9999;
  am.style.transition = `all ${time}ms ease`;
  am.style.opacity = "1";
  am.textContent = btn.textContent;
  document.body.appendChild(am);

  // Force reflow for transition
  void am.offsetWidth;

  // Animate to input using translate instead of top/left
  am.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
  am.style.opacity = opacity;

  setTimeout(() => {
    am.remove();
  }, time);
}

/**
 * 返回上一页
 */
export function goBack() {
  const hcount = local?.hisotryMap?.[window.location.pathname] || 0;
  const count = window.history.length - hcount + 1;
  if (count > 0) {
    window.history.go(-count);
  } else {
    window.history.back();
  }
}

/**
 * 将对象转换为唯一的字符串key
 * @param {Object} obj - 需要生成key的对象
 * @returns {string} 唯一的key字符串
 */
export function generateObjectKey(obj) {
  // 将对象转换为有序的JSON字符串，确保属性顺序不影响结果
  const sortedJson = JSON.stringify(obj, Object.keys(obj || {}).sort());

  // 简单的哈希实现
  function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // 转换为32位整数
    }
    return Math.abs(hash).toString(16);
  }

  return simpleHash(sortedJson);
}


/**
 * 
 /
*/
export const getUiCode = () => {
  let c = 1
  // theme =  1 蓝色 2 橙白 3 为金白 4=紫白
  switch (import.meta.env.VITE_THEME) {
    case 'blue':
      c = 1
      break;
    case 'orange':
      c = 2
      break;
    case 'gold':
      c = 3
      break;
    case 'purple':
      c = 4
      break;
    default:
      c = 1
  }
  return c
}